/**
 * İşçilik Alacakları hesap motoru
 * ---------------------------------------------------------------
 * Parametreler resmî kaynaklardan doğrulanmıştır (2026-09-04):
 *
 *  • Gelir vergisi tarifesi (ücret): 31.12.2025 tarihli Resmî Gazete
 *    (5. mükerrer) Gelir Vergisi Genel Tebliği
 *  • Asgari ücret / SGK taban–tavan: 2026 bordro parametreleri
 *  • Kıdem tazminatı tavanı: memur maaş katsayısına bağlı, 6 ayda bir
 *  • Damga vergisi: binde 7,59 (0,00759)
 *
 * Yeni yıl geldiğinde yalnızca aşağıdaki tablolar güncellenir.
 */

/* ---------------- Parametre tabloları ---------------- */

/** Ücret gelirleri gelir vergisi tarifesi (kümülatif matraha göre). */
export const GELIR_VERGISI_TARIFESI = {
  2026: [
    { ustSinir: 190000, oran: 15 },
    { ustSinir: 400000, oran: 20 },
    { ustSinir: 1500000, oran: 27 },
    { ustSinir: 5300000, oran: 35 },
    { ustSinir: Infinity, oran: 40 },
  ],
};

/** Kıdem tazminatı tavanı — dönem başlangıcına göre. */
export const KIDEM_TAVANI = [
  { baslangic: '2026-01-01', tutar: 64948.77 },
  { baslangic: '2026-07-01', tutar: 73729.87 },
];

export const SGK_ISCI_ORANI = 14;        // %14 (MYÖ %9 + GSS %5)
export const ISSIZLIK_ISCI_ORANI = 1;    // %1
export const DAMGA_ORANI = 0.00759;      // binde 7,59

export const ASGARI_BRUT_2026 = 33030.00;
export const SGK_TAVAN_2026 = 297270.00; // asgari brütün 7,5 katı değil; resmî tavan

/** Asgari ücret istisnası: asgari ücrete isabet eden vergi ve damga alınmaz. */
export const ASGARI_UCRET_ISTISNASI = true;

/* ---------------- Yardımcılar ---------------- */

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num = (v) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  let s = String(v).trim().replace(/\s/g, '');
  if (!s) return 0;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

export const parseTutar = num;

export const formatTL = (n) =>
  (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function parseDate(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const s = String(v).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Kıdem tazminatı tavanı — çıkış tarihindeki dönem. */
export function getKidemTavani(cikisTarihi) {
  const d = parseDate(cikisTarihi) || new Date();
  let secili = KIDEM_TAVANI[0];
  for (const row of KIDEM_TAVANI) {
    if (parseDate(row.baslangic) <= d) secili = row;
  }
  return secili ? secili.tutar : 0;
}

function tarife(yil) {
  return GELIR_VERGISI_TARIFESI[yil] || GELIR_VERGISI_TARIFESI[2026];
}

/**
 * Kümülatif matrah dikkate alınarak gelir vergisi.
 * @param {number} matrah      Bu ödemenin vergi matrahı
 * @param {number} oncekiMatrah Yıl içinde önceden oluşmuş kümülatif matrah
 */
export function gelirVergisi(matrah, oncekiMatrah = 0, yil = 2026) {
  const dilimler = tarife(yil);
  let kalan = Math.max(0, num(matrah));
  let konum = Math.max(0, num(oncekiMatrah));
  let vergi = 0;
  const kirilim = [];

  for (const d of dilimler) {
    if (kalan <= 0) break;
    if (konum >= d.ustSinir) continue;
    const dilimdeKalan = d.ustSinir - konum;
    const uygulanan = Math.min(kalan, dilimdeKalan);
    const tutar = uygulanan * (d.oran / 100);
    vergi += tutar;
    kirilim.push({ oran: d.oran, matrah: round2(uygulanan), vergi: round2(tutar) });
    konum += uygulanan;
    kalan -= uygulanan;
  }
  return { vergi: round2(vergi), kirilim };
}

/* ---------------- Brüt ↔ Net ---------------- */

/**
 * Aylık brüt ücretten net ücret.
 * Asgari ücret istisnası uygulanır: asgari ücrete isabet eden gelir
 * ve damga vergisi kesilmez (7349 s.K.).
 */
export function brutToNet(brut, { oncekiMatrah = 0, yil = 2026, istisna = ASGARI_UCRET_ISTISNASI } = {}) {
  const b = num(brut);
  if (b <= 0) return null;

  const sgkMatrah = Math.min(b, SGK_TAVAN_2026);
  const sgk = sgkMatrah * (SGK_ISCI_ORANI / 100);
  const issizlik = sgkMatrah * (ISSIZLIK_ISCI_ORANI / 100);
  const vergiMatrahi = b - sgk - issizlik;

  const gv = gelirVergisi(vergiMatrahi, oncekiMatrah, yil);
  const damga = b * DAMGA_ORANI;

  // Asgari ücret istisnası: asgari ücretin vergi ve damgası düşülür
  let istisnaGV = 0;
  let istisnaDamga = 0;
  if (istisna) {
    const aSgk = ASGARI_BRUT_2026 * ((SGK_ISCI_ORANI + ISSIZLIK_ISCI_ORANI) / 100);
    const aMatrah = ASGARI_BRUT_2026 - aSgk;
    istisnaGV = Math.min(gv.vergi, gelirVergisi(aMatrah, oncekiMatrah, yil).vergi);
    istisnaDamga = Math.min(damga, ASGARI_BRUT_2026 * DAMGA_ORANI);
  }

  const gvNet = Math.max(0, gv.vergi - istisnaGV);
  const damgaNet = Math.max(0, damga - istisnaDamga);
  const net = b - sgk - issizlik - gvNet - damgaNet;

  return {
    brut: round2(b),
    sgk: round2(sgk),
    issizlik: round2(issizlik),
    vergiMatrahi: round2(vergiMatrahi),
    gelirVergisi: round2(gvNet),
    gelirVergisiIstisna: round2(istisnaGV),
    damga: round2(damgaNet),
    damgaIstisna: round2(istisnaDamga),
    net: round2(net),
    kirilim: gv.kirilim,
  };
}

/**
 * Net ücretten brüt ücret — kapalı çözüm yerine ikili arama.
 * Kademeli vergi tarifesi ve istisna nedeniyle ters fonksiyon
 * doğrudan yazılamaz; 60 adımda kuruş altına iner.
 */
export function netToBrut(net, opts = {}) {
  const hedef = num(net);
  if (hedef <= 0) return null;

  let alt = hedef;
  let ust = hedef * 3 + 10000;
  let sonuc = null;

  for (let i = 0; i < 60; i += 1) {
    const orta = (alt + ust) / 2;
    const r = brutToNet(orta, opts);
    if (!r) break;
    sonuc = r;
    if (Math.abs(r.net - hedef) < 0.005) break;
    if (r.net < hedef) alt = orta;
    else ust = orta;
  }
  return sonuc;
}

/* ---------------- Kıdem / İhbar ---------------- */

/**
 * Gün cinsinden çalışma süresi.
 * @param {boolean} uclarDahil  true  = giriş ve çıkış günü de sayılır (+1)
 *                              false = iki tarih arası fark (yaygın bilirkişi yöntemi)
 *
 * NOT: Bu tercih kıdem tazminatının kıst kısmını doğrudan etkiler.
 * 47.200 TL brüt ve ~1,6 yıllık kıdemde iki yöntem arasındaki fark
 * yaklaşık 129 TL'dir. Hesaplayıcılar bu konuda ayrışır; bu yüzden
 * seçenek olarak bırakıldı. Varsayılan: uçlar hariç.
 */
export function calismaGunu(giris, cikis, uclarDahil = false) {
  const a = parseDate(giris);
  const b = parseDate(cikis);
  if (!a || !b || b < a) return 0;
  return Math.round((b - a) / 86400000) + (uclarDahil ? 1 : 0);
}

export function kidemSuresi(giris, cikis, uclarDahil = false) {
  const gun = calismaGunu(giris, cikis, uclarDahil);
  const yil = Math.floor(gun / 365);
  const kalanGun = gun - yil * 365;
  return { toplamGun: gun, yil, kalanGun };
}

/**
 * Kıdem tazminatı — giydirilmiş BRÜT günlük ücret üzerinden,
 * her tam yıl için 30 günlük ücret; artan süre orantılı.
 * Tavanı aşan giydirilmiş ücret tavana çekilir (1475 s.K. m.14).
 * Kesinti: yalnızca damga vergisi.
 */
export function hesaplaKidem({ giydirilmisAylikBrut, giris, cikis, uclarDahil = false }) {
  const sure = kidemSuresi(giris, cikis, uclarDahil);
  if (sure.toplamGun <= 0) return null;

  const tavan = getKidemTavani(cikis);
  const ham = num(giydirilmisAylikBrut);
  const esasAylik = Math.min(ham, tavan);
  const tavanUygulandi = ham > tavan;

  const yilKismi = esasAylik * sure.yil;
  const kalanKismi = (esasAylik / 365) * sure.kalanGun;
  const brut = yilKismi + kalanKismi;
  const damga = brut * DAMGA_ORANI;

  return {
    tur: 'Kıdem Tazminatı',
    sure,
    tavan,
    tavanUygulandi,
    esasAylik: round2(esasAylik),
    brut: round2(brut),
    damga: round2(damga),
    gelirVergisi: 0,
    net: round2(brut - damga),
    aciklama: `${sure.yil} yıl ${sure.kalanGun} gün · giydirilmiş brüt ${formatTL(esasAylik)} TL`,
  };
}

/**
 * İhbar süresi (hafta) — 4857 s.K. m.17
 *   6 aydan az            : 2 hafta
 *   6 ay – 1,5 yıl        : 4 hafta
 *   1,5 yıl – 3 yıl       : 6 hafta
 *   3 yıldan FAZLA        : 8 hafta
 *
 * NOT: Eşikler takvim üzerinden karşılaştırılır. "gün/30" yaklaşımı
 * kullanılırsa tam 3 yıl 36,5 ay çıkıp yanlışlıkla 8 haftaya düşüyordu;
 * kanun "üç yıla kadar" için 6 hafta diyor.
 */
export function ihbarHaftasi(giris, cikis) {
  const a = parseDate(giris);
  const b = parseDate(cikis);
  if (!a || !b || b < a) return 0;

  // a + n ay <= b  ise n ay tamamlanmıştır (takvim esaslı)
  const ayEkle = (d, n) => {
    const x = new Date(d.getFullYear(), d.getMonth() + n, d.getDate());
    // Ayın son günü taşmalarını düzelt (31 Ocak + 1 ay gibi)
    if (x.getDate() !== d.getDate()) x.setDate(0);
    return x;
  };
  const tamamlandi = (n) => ayEkle(a, n) <= b;

  if (!tamamlandi(6)) return 2;
  if (!tamamlandi(18)) return 4;
  if (!tamamlandi(36)) return 6;
  // Tam 36 ay "üç yıla kadar" sayılır; 8 hafta için ÜZERİ olmalı
  return ayEkle(a, 36).getTime() === b.getTime() ? 6 : 8;
}

/**
 * İhbar tazminatı — giydirilmiş brüt günlük ücret × ihbar süresi.
 * Kesinti: gelir vergisi + damga vergisi.
 */
export function hesaplaIhbar({ giydirilmisAylikBrut, giris, cikis, oncekiMatrah = 0, yil = 2026 }) {
  const hafta = ihbarHaftasi(giris, cikis);
  const aylik = num(giydirilmisAylikBrut);
  if (aylik <= 0 || !hafta) return null;

  const gunluk = aylik / 30;
  const brut = gunluk * hafta * 7;
  const gv = gelirVergisi(brut, oncekiMatrah, yil);
  const damga = brut * DAMGA_ORANI;

  return {
    tur: 'İhbar Tazminatı',
    hafta,
    brut: round2(brut),
    gelirVergisi: round2(gv.vergi),
    damga: round2(damga),
    net: round2(brut - gv.vergi - damga),
    aciklama: `${hafta} hafta · günlük brüt ${formatTL(gunluk)} TL`,
  };
}

/* ---------------- Diğer alacaklar ---------------- */

/** Kullanılmayan yıllık izin — çıplak brüt günlük ücret × gün. */
export function hesaplaYillikIzin({ ciplakAylikBrut, gun, oncekiMatrah = 0, yil = 2026 }) {
  const aylik = num(ciplakAylikBrut);
  const g = num(gun);
  if (aylik <= 0 || g <= 0) return null;
  const gunluk = aylik / 30;
  const brut = gunluk * g;
  const gv = gelirVergisi(brut, oncekiMatrah, yil);
  const damga = brut * DAMGA_ORANI;
  return {
    tur: 'Yıllık İzin Ücreti',
    brut: round2(brut),
    gelirVergisi: round2(gv.vergi),
    damga: round2(damga),
    net: round2(brut - gv.vergi - damga),
    aciklama: `${g} gün · günlük brüt ${formatTL(gunluk)} TL`,
  };
}

/**
 * Fazla mesai — saatlik ücretin %50 fazlasıyla (4857 m.41).
 * Saatlik ücret = aylık brüt / 225 (haftalık 45 saat esası).
 */
export function hesaplaFazlaMesai({ ciplakAylikBrut, saat, zamOrani = 50, oncekiMatrah = 0, yil = 2026 }) {
  const aylik = num(ciplakAylikBrut);
  const s = num(saat);
  if (aylik <= 0 || s <= 0) return null;
  const saatlik = aylik / 225;
  const brut = saatlik * (1 + num(zamOrani) / 100) * s;
  const gv = gelirVergisi(brut, oncekiMatrah, yil);
  const damga = brut * DAMGA_ORANI;
  return {
    tur: 'Fazla Mesai',
    brut: round2(brut),
    gelirVergisi: round2(gv.vergi),
    damga: round2(damga),
    net: round2(brut - gv.vergi - damga),
    aciklama: `${s} saat · saatlik brüt ${formatTL(saatlik)} TL · %${zamOrani} zamlı`,
  };
}

/**
 * UBGT (ulusal bayram / genel tatil) — çalışılan her gün için
 * bir günlük ilave ücret (4857 m.47).
 */
export function hesaplaUbgt({ ciplakAylikBrut, gun, oncekiMatrah = 0, yil = 2026 }) {
  const aylik = num(ciplakAylikBrut);
  const g = num(gun);
  if (aylik <= 0 || g <= 0) return null;
  const gunluk = aylik / 30;
  const brut = gunluk * g;
  const gv = gelirVergisi(brut, oncekiMatrah, yil);
  const damga = brut * DAMGA_ORANI;
  return {
    tur: 'UBGT Alacağı',
    brut: round2(brut),
    gelirVergisi: round2(gv.vergi),
    damga: round2(damga),
    net: round2(brut - gv.vergi - damga),
    aciklama: `${g} gün · günlük brüt ${formatTL(gunluk)} TL`,
  };
}

/** Hafta tatili — çalışılan her gün için %150 zamlı ücret (4857 m.46). */
export function hesaplaHaftaTatili({ ciplakAylikBrut, gun, oncekiMatrah = 0, yil = 2026 }) {
  const aylik = num(ciplakAylikBrut);
  const g = num(gun);
  if (aylik <= 0 || g <= 0) return null;
  const gunluk = aylik / 30;
  const brut = gunluk * 1.5 * g;
  const gv = gelirVergisi(brut, oncekiMatrah, yil);
  const damga = brut * DAMGA_ORANI;
  return {
    tur: 'Hafta Tatili Alacağı',
    brut: round2(brut),
    gelirVergisi: round2(gv.vergi),
    damga: round2(damga),
    net: round2(brut - gv.vergi - damga),
    aciklama: `${g} gün · günlük brüt ${formatTL(gunluk)} TL · %150 zamlı`,
  };
}

/* ---------------- Toplu hesap ---------------- */

export function hesaplaTumAlacaklar(girdi) {
  const {
    ciplakAylikBrut, giydirilmisAylikBrut, giris, cikis,
    kidem = true, ihbar = true,
    yillikIzinGun = 0, fazlaMesaiSaat = 0, ubgtGun = 0, haftaTatiliGun = 0,
    yil = 2026, uclarDahil = false,
  } = girdi || {};

  const giydirilmis = num(giydirilmisAylikBrut) || num(ciplakAylikBrut);
  const ciplak = num(ciplakAylikBrut) || giydirilmis;

  const kalemler = [];
  if (kidem) {
    const k = hesaplaKidem({ giydirilmisAylikBrut: giydirilmis, giris, cikis, uclarDahil });
    if (k) kalemler.push(k);
  }
  if (ihbar) {
    const i = hesaplaIhbar({ giydirilmisAylikBrut: giydirilmis, giris, cikis, yil });
    if (i) kalemler.push(i);
  }
  const izin = hesaplaYillikIzin({ ciplakAylikBrut: ciplak, gun: yillikIzinGun, yil });
  if (izin) kalemler.push(izin);
  const fm = hesaplaFazlaMesai({ ciplakAylikBrut: ciplak, saat: fazlaMesaiSaat, yil });
  if (fm) kalemler.push(fm);
  const ub = hesaplaUbgt({ ciplakAylikBrut: ciplak, gun: ubgtGun, yil });
  if (ub) kalemler.push(ub);
  const ht = hesaplaHaftaTatili({ ciplakAylikBrut: ciplak, gun: haftaTatiliGun, yil });
  if (ht) kalemler.push(ht);

  const toplam = kalemler.reduce(
    (t, k) => ({
      brut: t.brut + k.brut,
      gelirVergisi: t.gelirVergisi + (k.gelirVergisi || 0),
      damga: t.damga + (k.damga || 0),
      net: t.net + k.net,
    }),
    { brut: 0, gelirVergisi: 0, damga: 0, net: 0 }
  );

  return {
    kalemler,
    toplam: {
      brut: round2(toplam.brut),
      gelirVergisi: round2(toplam.gelirVergisi),
      damga: round2(toplam.damga),
      net: round2(toplam.net),
    },
    sure: kidemSuresi(giris, cikis, uclarDahil),
    tavan: getKidemTavani(cikis),
  };
}
